import { useEffect, useState, ChangeEvent } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";


import logocontinente from "@/assets/images/logo-continente.png";

import Header from "@/components/header/Header";
import UsernameInput from "@/components/username-input/UsernameInput";

import { UserCredentials } from "@/models/UserCredentials";
import { FormError } from "@/models/FormError";
import { loginService } from "@/services/LoginService";
import { userService } from "@/services/UserService";
import { authorizationService } from "@/services/AuthorizationService";
import { useAuthorizeQueryParams } from "../hooks/useAuthorizeQueryParams";

import { Button } from "@/components/ui/button";
import LoadingLogo from "@/components/loading-logo/loading-logo";
import Input from "@/components/ui/Input";
import { useTranslation } from "react-i18next";

type Form = UserCredentials & FormError;

const requiredFieldError = "Campo de preenchimento obrigatório.";
const invalidEmailError = "Formato de e-mail inválido.";
const invalidCredentialsError =
  "E-mail ou palavra-passe incorrectos. Verifique os dados e tente novamente.";
const unexpectedError =
  "Temporariamente indisponivel. Por favor tente mais tarde.";

const usernameValidator = (register: UseFormRegister<Form>) =>
  register("username", {
    required: { value: true, message: requiredFieldError },
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: invalidEmailError,
    },
  });

const passwordValidator = (register: UseFormRegister<Form>) =>
  register("password", {
    required: {
      value: true,
      message: requiredFieldError,
    },
  });

export default function SocialLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  const authorizeQueryParams = useAuthorizeQueryParams();

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit"
  });


  useEffect(() => {
    const getUser = async () => {
      const resp = await userService.get();

      switch (resp.kind) {
        case "success": {
          const redirectUrl =
            authorizationService.generateUrl(authorizeQueryParams);
          window.location.href = redirectUrl.toString();
          break;
        }
        default:
          setLoading(false);
          break;
      }
    };

    getUser();
  }, []);

  useEffect(() => {
    passwordValidator(register);
    usernameValidator(register);
  }, [register]);

  const handleUsernameChange = (input: string, phoneNumberCode?: string) => {
    setUsername(input);
    setValue("username", input);
  };

  const handlePasswordChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setPassword(value);
    setValue("password", value);
  };

  const onSubmit: SubmitHandler<UserCredentials> = async (data) => {
    setLoading(true);

    try {
      const response = await loginService.login({
        username: data.username,
        password: data.password,
      });

      if (response.kind === "success") {
        const redirectUrl =
          authorizationService.generateUrl(authorizeQueryParams);
        window.location.href = redirectUrl.toString();

        return;
      }

      switch (response.kind) {
        case "error":
          setError("submitError", { message: invalidCredentialsError });
          setLoading(false);
          break;
        default:
          setError("submitError", { message: unexpectedError });
          setLoading(false);
          break;
      }
    } catch (e) {
      setError("submitError", { message: unexpectedError });
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingLogo />}
      <form
        id="socialLogin"
        onSubmit={(e) => {
          clearErrors();
          handleSubmit(onSubmit)(e);
        }}
      >
        <Header
          showBackButton={false}
          topLogo={logocontinente}
          title="Utilize o seu registo Continente Online"
        />
        <div>
          <div className="socialLogin_body_text">
            <p>
              Caso opte pelo registo no site da Wells com o email e palavra
              passe da sua conta Continente Online,{" "}
              <b>o seu login deverá ser sempre feito por esta via. </b>
            </p>
            <p>
              Para prosseguir com este registo, preencha os campos abaixo e
              clique em Continuar.
            </p>
          </div>
          <div className="socialLogin_inputs">
            <div className="grid gap-4">
              <UsernameInput
                required
                ariaLabel={t("accessibility.signin.input")}
                type="email"
                isError={!!errors.username}
                errorMsg={errors.username?.message}
                disabled={loading}
                value={username}
                onChange={handleUsernameChange}
                autocompleteWith="email" isFilled={false} />
              <Input
                id="password_input"
                name="password_input"
                type={"password"}
                value={password}
                onChange={handlePasswordChange}
                required={true}
                disabled={loading}
                labelText={t("login_password_associated.password_input")}
                isError={!!(errors.password || errors.submitError)}
                errorMsg={errors.password?.message ||
                  errors.submitError?.message ||
                  ""}
                autoComplete="current-password"
                isFilled={false} />
            </div>
          </div>
          <div className="socialLogin_buttons">
            <Button
              disabled={loading}>
              CONTINUAR
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
